import { useStore } from 'vuex';
import { store as importedStore } from '@/store'
import { PERMISSION_PRIMITIVES } from '@/config/constants';

/**
 * Checks if a transaction occurred before same-day batch close (midnight ET)
 * @param {string} transactionDate - ISO date string of when transaction occurred
 * @returns {boolean}
 */
function isBeforeBatchClose(transactionDate) {
  const txDate = new Date(transactionDate);
  const now = new Date();

  // Get ET offset accounting for daylight saving time
  const getETOffset = (date) => {
    // ET is UTC-5 in standard time, UTC-4 in daylight time
    const jan = new Date(date.getFullYear(), 0, 1);
    const jul = new Date(date.getFullYear(), 6, 1);
    const stdOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
    const isDST = date.getTimezoneOffset() < stdOffset;
    return isDST ? -4 : -5;
  };

  // Convert to ET (Eastern Time)
  const txOffset = getETOffset(txDate);
  const nowOffset = getETOffset(now);

  const txDateET = new Date(txDate.getTime() + (txDate.getTimezoneOffset() + txOffset * 60) * 60000);
  const nowET = new Date(now.getTime() + (now.getTimezoneOffset() + nowOffset * 60) * 60000);

  // Get midnight ET for the transaction date
  const txMidnightET = new Date(txDateET);
  txMidnightET.setHours(23, 59, 59, 999);

  // If the transaction has already passed its batch close time, it needs refund
  if (nowET > txMidnightET) {
    return false;
  }

  // If we're still on the same day and before midnight, void is available
  return true;
}

/**
 * Determines if void action should be shown for a transaction
 * @param {Object} input
 * @param {ReportTransaction} input.txData
 * @param {import('vue').Ref<boolean> | { value: boolean }} input.hasPermissions
 * @param {import('vue').Ref<boolean> | { value: boolean }} input.userHasVoidPermission
 * @param {import('vue').Ref<Set<string>> | { value: Set<string> }} input.userHasVoidPermission_to_merchants
 * @param {import('vue').Ref<boolean> | { value: boolean }} input.userHasVoidCardPermission
 * @param {import('vue').Ref<Set<string>> | { value: Set<string> }} input.userHasVoidCardPermission_to_merchants
 * @param {import('vue').Ref<boolean> | { value: boolean }} input.userHasVoidAchPermission
 * @param {import('vue').Ref<Set<string>> | { value: Set<string> }} input.userHasVoidAchPermission_to_merchants
 * @returns {boolean}
 */
export function shouldShowVoid({
                                 txData,
                                 hasPermissions,
                                 userHasVoidPermission,
                                 userHasVoidPermission_to_merchants,
                                 userHasVoidCardPermission,
                                 userHasVoidCardPermission_to_merchants,
                                 userHasVoidAchPermission,
                                 userHasVoidAchPermission_to_merchants
                               }) {
  // Check if transaction is eligible for void (successful, not already voided/refunded)
  if (!isTransactionEligibleForAction(txData)) {
    return false;
  }

  // Check if transaction is before batch close
  if (!isBeforeBatchClose(txData.created_at)) {
    return false;
  }

  // If client doesn't have roles/permissions enabled, don't show void
  // (maintain existing behavior where they only get refund option)
  if (!hasPermissions?.value) {
    return false;
  }

  // Check void permissions
  if (!userHasVoidPermission?.value) {
    return false;
  }

  const merchantId = txData.client_id || txData.client?.id;

  // Check merchant-specific void permission
  if (!userHasVoidPermission_to_merchants?.value?.has(merchantId)) {
    return false;
  }

  // Check payment method specific permissions
  const isCardTransaction = ['cc', 'credit', 'card', 'terminal', 'charge', 'authorize', 'terminal_charge'].includes(txData.type);
  const isAchTransaction = ['debit', 'ach'].includes(txData.type);
  const isCashTransaction = ['cash'].includes(txData.type);

  if (isCardTransaction) {
    return userHasVoidCardPermission?.value &&
      userHasVoidCardPermission_to_merchants?.value?.has(merchantId);
  }

  if (isAchTransaction) {
    return userHasVoidAchPermission?.value &&
      userHasVoidAchPermission_to_merchants?.value?.has(merchantId);
  }

  if (isCashTransaction) {
    return userHasVoidCardPermission?.value &&
        userHasVoidCardPermission_to_merchants?.value?.has(merchantId);
  }

  return false;
}

/**
 * Common logic to check if transaction is eligible for void/refund
 * @param {ReportTransaction} txData
 * @returns {boolean}
 */
function isTransactionEligibleForAction(txData) {
  // Check if transaction was successful
  const isSuccess = (
    // payment status
    !(`${txData?.payment_status}` in {'false': true, 'False': true}) &&
    // request status
    (txData?.request_status === true)
  );

  // Check if it's not already a negative transaction
  const isNegative = (txData?.type in {
    'void': true,
    'refund': true,
    'ach_refund': true,
    'credit': true,
    'blind_refund': true,
    'reversal': true
  });

  return isSuccess && !isNegative;
}

/**
 * returns true when the transaction is a successful transaction and not a negative one
 * @param {Object} input
 * @param {ReportTransaction} input.txData
 */
export function shouldShowRefund2({ txData }) {
  try {
    let shouldShowInPrincipal = shouldShowRefund2Unsafe({ txData });

    let store = useStore() || importedStore;

    if (store.getters.merchantHasPermissions) {
      return shouldShowInPrincipal && store.getters.hasPermissionOnMerchant(PERMISSION_PRIMITIVES.TRANSACTION_REFUND, txData.client_id || txData.client?.id);
    }

    return shouldShowInPrincipal;
  } catch (ignored) {
    console.log('shouldShowRefund2Unsafe')
    return false
  }
}

function shouldShowRefund2Unsafe({ txData }) {
  return isTransactionEligibleForAction(txData);
}

/**
 * Updated shouldShowRefund to check new refund permissions and batch close timing
 * @param {Object} input
 * @param {ReportTransaction} input.txData
 * @param {import('vue').Ref<boolean> | { value: boolean }} input.hasPermissions
 * @param {import('vue').Ref<boolean> | { value: boolean }} input.userHasAchRefundPermission
 * @param {import('vue').Ref<Set<string>> | { value: Set<string> }} input.userHasAchRefundPermission_to_merchants
 * @param {import('vue').Ref<boolean> | { value: boolean }} input.userHasCreditCardRefundPermission
 * @param {import('vue').Ref<Set<string>> | { value: Set<string> }} input.userHasCreditCardRefundPermission_to_merchants
 * @param {import('vue').Ref<boolean> | { value: boolean }} input.userHasRefundCardPermission
 * @param {import('vue').Ref<Set<string>> | { value: Set<string> }} input.userHasRefundCardPermission_to_merchants
 * @param {import('vue').Ref<boolean> | { value: boolean }} input.userHasRefundAchPermission
 * @param {import('vue').Ref<Set<string>> | { value: Set<string> }} input.userHasRefundAchPermission_to_merchants
 * @returns {boolean}
 */
export function shouldShowRefund({
                                   txData,
                                   hasPermissions,
                                   userHasAchRefundPermission,
                                   userHasAchRefundPermission_to_merchants,
                                   userHasCreditCardRefundPermission,
                                   userHasCreditCardRefundPermission_to_merchants,
                                   userHasRefundCardPermission,
                                   userHasRefundCardPermission_to_merchants,
                                   userHasRefundAchPermission,
                                   userHasRefundAchPermission_to_merchants
                                 }) {
  // Check if transaction is eligible for refund
  if (!isTransactionEligibleForAction(txData)) {
    return false;
  }

  const isCardTransaction = ['cc', 'credit', 'card', 'terminal', 'charge', 'authorize', 'terminal_charge'].includes(txData.type);
  const isAchTransaction = ['debit', 'ach'].includes(txData.type);
  const isCashTransaction = ['cash'].includes(txData.type);

  // If client doesn't have roles/permissions enabled, maintain existing behavior
  if (!hasPermissions?.value) {
    // // Skip batch close check for non-permission clients (existing behavior)
    // // Just check for the legacy refund permissions
    // if (isCardTransaction) {
    //   return userHasCreditCardRefundPermission?.value;
    // }
    // if (isAchTransaction) {
    //   return userHasAchRefundPermission?.value;
    // }
    return true; // Default to allowing refunds for non-permission clients
  }

  // For clients with permissions enabled, check batch close timing
  if (isBeforeBatchClose(txData.created_at)) {
    return false; // Should use void instead
  }

  const merchantId = txData.client_id || txData.client?.id;

  // Determine which generic refund permission to check
  const hasGenericRefundPermission = isCardTransaction
    ? userHasCreditCardRefundPermission?.value
    : userHasAchRefundPermission?.value;

  // User must have the generic refund permission first
  if (!hasGenericRefundPermission) {
    return false;
  }

  // Check merchant-specific generic permission
  const hasGenericMerchantPermission = isCardTransaction
    ? userHasCreditCardRefundPermission_to_merchants?.value?.has(merchantId)
    : userHasAchRefundPermission_to_merchants?.value?.has(merchantId);

  if (!hasGenericMerchantPermission) {
    return false;
  }

  // Now check for the new specific refund permissions
  if (isCardTransaction && userHasRefundCardPermission?.value) {
    return userHasRefundCardPermission_to_merchants?.value?.has(merchantId);
  }

  if (isAchTransaction && userHasRefundAchPermission?.value) {
    return userHasRefundAchPermission_to_merchants?.value?.has(merchantId);
  }

  if (isCashTransaction && userHasRefundCardPermission?.value) {
    return userHasRefundCardPermission_to_merchants?.value?.has(merchantId);
  }

  // If user has generic permission but not the new specific permission, deny access
  return false;
}